import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

const FUTURE_TEST_API_PREFIX = 'future-test/';

export type FutureTestModality =
  | 'FBS'
  | 'BLOODS'
  | 'DEXA'
  | 'PRS'
  | 'MRI'
  | 'CT_ANGIOGRAM'
  | 'ULTRASOUND'
  | 'X_RAY';

export type FutureTestPayload = {
  member_id: string;
  /** Full date (day, month, year) as ISO string. Null when month-only is selected. */
  scheduled_date: string | null;
  /** Month + year only, format YYYY-MM. Null when a full date is selected. */
  scheduled_month_year: string | null;
  modality: FutureTestModality | string;
  panel_ids?: number[];
  body_parts?: string[];
  notes?: string;
};

export type FutureTestUpdatePayload = {
  id: string;
  scheduled_date?: string | null;
  scheduled_month_year?: string | null;
  modality?: FutureTestModality | string;
  panel_ids?: number[];
  body_parts?: string[];
  notes?: string;
  member_id?: string;
};

export type FutureTestListParams = {
  member_id?: string;
  limit?: number;
  offset?: number;
};

export type FutureTestItem = {
  id: string | number;
  member_id: string;
  scheduled_date?: string | null;
  scheduled_month_year?: string | null;
  modality?: FutureTestModality | string;
  panel_id?: number | string;
  panel_ids?: (number | string)[];
  panel?: {
    id: number | string;
    name?: string;
  };
  panels?: Array<{
    id: number | string;
    name?: string;
  }>;
  body_parts?: string[];
  notes?: string;
  created_at?: string;
  updated_at?: string;
};

export type FutureTestListResponse = {
  results: FutureTestItem[];
  total?: number;
};

export const deleteFutureTest = async (id: string | number): Promise<void> => {
  await axiosInstance.delete(`${API_V1_PREFIX}${FUTURE_TEST_API_PREFIX}`, {
    data: { id },
  });
};

export const createFutureTest = async (
  payload: FutureTestPayload
): Promise<FutureTestItem> => {
  const { data } = await axiosInstance.post(
    `${API_V1_PREFIX}${FUTURE_TEST_API_PREFIX}`,
    payload
  );
  return data;
};

export const getFutureTests = async (
  params: FutureTestListParams = {}
): Promise<FutureTestListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.member_id) searchParams.append('member_id', params.member_id);
  if (typeof params.limit === 'number') searchParams.append('limit', String(params.limit));
  if (typeof params.offset === 'number') searchParams.append('offset', String(params.offset));

  const query = searchParams.toString();
  const url = `${API_V1_PREFIX}${FUTURE_TEST_API_PREFIX}${query ? `?${query}` : ''}`;
  const { data } = await axiosInstance.get(url);
  return data;
};

export const getFutureTestById = async (
  id: string | number
): Promise<FutureTestItem> => {
  const { data } = await axiosInstance.get(
    `${API_V1_PREFIX}${FUTURE_TEST_API_PREFIX}${id}`
  );
  return data;
};

export const updateFutureTest = async (
  payload: FutureTestUpdatePayload
): Promise<FutureTestItem> => {
  const { data } = await axiosInstance.patch(
    `${API_V1_PREFIX}${FUTURE_TEST_API_PREFIX}`,
    payload
  );
  return data;
};
